/**
 * JWT-based auth module for Telegram Mini App.
 *
 * Flow:
 * 1. On app start, ensureSession: if access_token → use it; if refresh_token only → refresh; else /telegram
 * 2. Store tokens in localStorage, isolated by Telegram user id
 * 3. Use Bearer token for all API requests
 * 4. On 401, try to refresh token, then retry; if refresh fails → /telegram
 * 5. /telegram is called only when there are no valid tokens
 */

const TOKEN_SCOPE_PREFIX = "gamba_auth_scoped"
const LEGACY_TOKEN_SCOPE_PREFIX = "loops_auth_scoped"
const ACCESS_TOKEN_BASE_KEY = "access_token"
const REFRESH_TOKEN_BASE_KEY = "refresh_token"
const ACCESS_TOKEN_EXPIRES_AT_BASE_KEY = "access_token_expires_at"
const ACCOUNT_ID_BASE_KEY = "account_id"

export interface AuthTokens {
  access_token: string
  refresh_token: string
  access_token_expires_at?: number
  /** Account UUID — may be present in auth/refresh responses as external_account_id / account_id / user_id / id */
  external_account_id?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// In-memory token cache
//
// Eliminates repeated localStorage reads, Telegram SDK property-chain traversals,
// and JWT decoding on every request / component render. Cache is kept in sync by
// setTokens() and clearTokens(); lazily populated from localStorage on first read.
// ─────────────────────────────────────────────────────────────────────────────

/** undefined = not yet initialised; null = known to be absent */
let _memAccessToken: string | null | undefined = undefined
let _memRefreshToken: string | null | undefined = undefined
let _memExpiresAt: number | null | undefined = undefined
let _memUserId: string | null | undefined = undefined // decoded lazily from JWT
let _memAccountId: string | null | undefined = undefined // stored from auth response or JWT
let _devUserIdOverrideGetter: (() => string | null) | null = null

export function setDevUserIdOverrideGetter(
  getter: (() => string | null) | null
): void {
  if (!import.meta.env.DEV) return
  _devUserIdOverrideGetter = getter
}

function _populateCacheFromStorage(): void {
  _memAccessToken = readScopedValue(ACCESS_TOKEN_BASE_KEY)
  _memRefreshToken = readScopedValue(REFRESH_TOKEN_BASE_KEY)
  const raw = readScopedValue(ACCESS_TOKEN_EXPIRES_AT_BASE_KEY)
  const v = raw ? Number(raw) : null
  _memExpiresAt = v !== null && Number.isFinite(v) ? v : null
  _memUserId = undefined // will be decoded from _memAccessToken on first getUserIdFromToken()
  _memAccountId = readScopedValue(ACCOUNT_ID_BASE_KEY) ?? undefined
}

function _ensureCache(): void {
  if (_memAccessToken !== undefined) return
  // Only commit to storage if the Telegram userId is already available.
  // If not, skip — _memAccessToken stays undefined and we retry on the next
  // call (same as the original per-call localStorage read). This avoids
  // permanently caching null when the SDK hasn't initialised yet.
  if (!getCurrentTelegramUserId()) return
  _populateCacheFromStorage()
}

function buildScopedStorageKey(
  baseKey: string,
  telegramUserId: string,
  prefix = TOKEN_SCOPE_PREFIX
): string {
  return `${prefix}:${telegramUserId}:${baseKey}`
}

function parseTelegramUserIdFromInitData(initData: string): string | null {
  if (!initData) return null
  try {
    const params = new URLSearchParams(initData)
    const userRaw = params.get("user")
    if (!userRaw) return null
    const user = JSON.parse(userRaw) as {
      id?: number | string
      username?: string
    }
    const id = user?.id
    if (typeof id === "number" && Number.isFinite(id)) return String(id)
    if (typeof id === "string" && id.trim()) return id
    return null
  } catch {
    return null
  }
}

function parseTelegramUsernameFromInitData(initData: string): string | null {
  if (!initData) return null
  try {
    const params = new URLSearchParams(initData)
    const userRaw = params.get("user")
    if (!userRaw) return null
    const user = JSON.parse(userRaw) as { username?: string }
    if (typeof user.username !== "string" || !user.username.trim()) return null
    return normalizeTelegramUsername(user.username)
  } catch {
    return null
  }
}

/** Visible @username without leading `@` (empty if user has no username). */
export function normalizeTelegramUsername(username: string): string {
  return username.trim().replace(/^@+/, "")
}

function getCurrentTelegramUsername(): string | null {
  if (typeof window === "undefined") return null

  const sdkUsername = window.Telegram?.WebApp?.initDataUnsafe?.user?.username
  if (typeof sdkUsername === "string" && sdkUsername.trim()) {
    const normalized = normalizeTelegramUsername(sdkUsername)
    return normalized.length > 0 ? normalized : null
  }

  return parseTelegramUsernameFromInitData(
    window.Telegram?.WebApp?.initData ?? ""
  )
}

function getCurrentTelegramUserId(): string | null {
  if (typeof window === "undefined") return null

  const sdkUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
  if (typeof sdkUserId === "number" && Number.isFinite(sdkUserId)) {
    return String(sdkUserId)
  }

  return parseTelegramUserIdFromInitData(
    window.Telegram?.WebApp?.initData ?? ""
  )
}

/** Numeric Telegram user id from Mini App initData (string digits, e.g. `"123456789"`). */
export function getTelegramUserId(): string | null {
  return getCurrentTelegramUserId()
}

/** Telegram @username without `@`, or null if not set in initData. */
export function getTelegramUsername(): string | null {
  return getCurrentTelegramUsername()
}

/** Deep-link / referral payload from `initDataUnsafe.start_param`, if present. */
export function getTelegramStartParam(): string | null {
  if (typeof window === "undefined") return null
  const raw = window.Telegram?.WebApp?.initDataUnsafe?.start_param
  if (typeof raw !== "string") return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

function getScopedTokenKey(baseKey: string): string | null {
  const telegramUserId = getCurrentTelegramUserId()
  if (!telegramUserId) return null
  return buildScopedStorageKey(baseKey, telegramUserId)
}

function readScopedValue(baseKey: string): string | null {
  const key = getScopedTokenKey(baseKey)
  if (!key) return null
  const current = localStorage.getItem(key)
  if (current) return current

  const telegramUserId = getCurrentTelegramUserId()
  if (!telegramUserId) return null
  const legacyKey = buildScopedStorageKey(
    baseKey,
    telegramUserId,
    LEGACY_TOKEN_SCOPE_PREFIX
  )
  const legacy = localStorage.getItem(legacyKey)
  if (!legacy) return null

  localStorage.setItem(key, legacy)
  localStorage.removeItem(legacyKey)
  return legacy
}

function removeScopedValue(baseKey: string): void {
  const key = getScopedTokenKey(baseKey)
  if (key) localStorage.removeItem(key)

  const telegramUserId = getCurrentTelegramUserId()
  if (telegramUserId) {
    localStorage.removeItem(
      buildScopedStorageKey(
        baseKey,
        telegramUserId,
        LEGACY_TOKEN_SCOPE_PREFIX
      )
    )
  }
}

function writeScopedTokens(tokens: AuthTokens): boolean {
  const accessKey = getScopedTokenKey(ACCESS_TOKEN_BASE_KEY)
  const refreshKey = getScopedTokenKey(REFRESH_TOKEN_BASE_KEY)
  const accessExpiresAtKey = getScopedTokenKey(ACCESS_TOKEN_EXPIRES_AT_BASE_KEY)

  if (!accessKey || !refreshKey || !accessExpiresAtKey) {
    return false
  }

  localStorage.setItem(accessKey, tokens.access_token)
  localStorage.setItem(refreshKey, tokens.refresh_token)

  if (
    typeof tokens.access_token_expires_at === "number" &&
    Number.isFinite(tokens.access_token_expires_at)
  ) {
    localStorage.setItem(
      accessExpiresAtKey,
      String(tokens.access_token_expires_at)
    )
  } else {
    localStorage.removeItem(accessExpiresAtKey)
  }

  if (tokens.external_account_id) {
    const accountIdKey = getScopedTokenKey(ACCOUNT_ID_BASE_KEY)
    if (accountIdKey)
      localStorage.setItem(accountIdKey, tokens.external_account_id)
  }

  return true
}

// ─────────────────────────────────────────────────────────────────────────────
// Token Storage
// ─────────────────────────────────────────────────────────────────────────────

export function getAccessToken(): string | null {
  _ensureCache()
  return _memAccessToken ?? null
}

/** Decode JWT payload (no verification; backend validates). Returns sub as userId if present. */
export function getUserIdFromToken(): string | null {
  if (import.meta.env.DEV && _devUserIdOverrideGetter) {
    const overridden = _devUserIdOverrideGetter()
    if (overridden) return overridden
  }
  _ensureCache()
  if (!_memAccessToken) return null
  // Return cached userId if already decoded.
  if (_memUserId !== undefined) return _memUserId
  try {
    const [, payloadB64] = _memAccessToken.split(".")
    if (!payloadB64) {
      _memUserId = null
      return null
    }
    const payload = JSON.parse(
      atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
    ) as { sub?: string }
    _memUserId = typeof payload.sub === "string" ? payload.sub : null
  } catch {
    _memUserId = null
  }
  return _memUserId
}

/**
 * Returns the backend account UUID (externalAccountID) — used for Centrifugo balance channel.
 *
 * Resolution order:
 * 1. In-memory cache (set by setAccountId / setTokens / _populateCacheFromStorage)
 * 2. JWT payload — tries sub, external_account_id, account_id, user_id claims
 * 3. null — caller must fetch from API (e.g. GET /v1/auth/me)
 */
export function getAccountId(): string | null {
  // 1. Already resolved
  if (_memAccountId !== undefined) return _memAccountId

  // 2. Try to decode from JWT using common claim names
  _ensureCache()
  if (_memAccessToken) {
    try {
      const [, payloadB64] = _memAccessToken.split(".")
      if (payloadB64) {
        const payload = JSON.parse(
          atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
        ) as Record<string, unknown>
        const id =
          payload["sub"] ??
          payload["external_account_id"] ??
          payload["account_id"] ??
          payload["user_id"] ??
          payload["id"]
        if (typeof id === "string" && id.length > 0) {
          _memAccountId = id
          return _memAccountId
        }
      }
    } catch {
      // malformed JWT — continue
    }
  }

  // 3. Nothing found yet — stay undefined so callers can retry (e.g. after API fetch)
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Account ID — reactive resolution
//
// The UUID is set once from the auth/refresh response (or JWT) and resolves
// any waitForAccountId() callers. Cleared on logout.
// ─────────────────────────────────────────────────────────────────────────────

let _accountIdResolvers: Array<(id: string) => void> = []

/** Store the account UUID and notify any pending waitForAccountId() callers. */
export function setAccountId(id: string): void {
  _memAccountId = id
  const key = getScopedTokenKey(ACCOUNT_ID_BASE_KEY)
  if (key) {
    try {
      localStorage.setItem(key, id)
    } catch {
      /* ignore */
    }
  }
  for (const resolve of _accountIdResolvers) resolve(id)
  _accountIdResolvers = []
}

/**
 * Returns a Promise that resolves with the account UUID as soon as it is available.
 * Resolves immediately if already known; otherwise waits for setAccountId() to be called.
 * The promise never rejects — on logout the pending promise is simply abandoned.
 */
export function waitForAccountId(): Promise<string> {
  const current = getAccountId()
  if (current) return Promise.resolve(current)
  return new Promise<string>((resolve) => {
    _accountIdResolvers.push(resolve)
  })
}

/** Clear pending resolvers on logout so stale subscriptions don't fire. */
export function clearAccountIdResolvers(): void {
  _accountIdResolvers = []
}

export function getRefreshToken(): string | null {
  _ensureCache()
  return _memRefreshToken ?? null
}

export function getAccessTokenExpiresAt(): number | null {
  _ensureCache()
  return _memExpiresAt ?? null
}

export function isAccessTokenExpired(
  nowSec = Math.floor(Date.now() / 1000)
): boolean {
  _ensureCache()
  if (_memExpiresAt == null) return false
  return _memExpiresAt <= nowSec
}

export function willAccessTokenExpireSoon(
  windowSec: number,
  nowSec = Math.floor(Date.now() / 1000)
): boolean {
  _ensureCache()
  if (_memExpiresAt == null) return false
  return _memExpiresAt - nowSec <= windowSec
}

export function setTokens(tokens: AuthTokens): void {
  try {
    if (!writeScopedTokens(tokens)) {
      console.warn("[Auth] Cannot save tokens: Telegram user id is unavailable")
      return
    }

    // Keep in-memory cache in sync — avoids localStorage round-trips on next request.
    _memAccessToken = tokens.access_token
    _memRefreshToken = tokens.refresh_token
    _memExpiresAt =
      typeof tokens.access_token_expires_at === "number" &&
      Number.isFinite(tokens.access_token_expires_at)
        ? tokens.access_token_expires_at
        : null
    _memUserId = undefined // decoded lazily on next getUserIdFromToken()
    if (tokens.external_account_id) {
      _memAccountId = tokens.external_account_id
    }

    if (import.meta.env.DEV) {
      console.log("[Auth] Tokens saved to scoped localStorage keys")
    }
  } catch (e) {
    console.error("[Auth] Failed to save tokens:", e)
  }
}

export function clearTokens(): void {
  try {
    removeScopedValue(ACCESS_TOKEN_BASE_KEY)
    removeScopedValue(REFRESH_TOKEN_BASE_KEY)
    removeScopedValue(ACCESS_TOKEN_EXPIRES_AT_BASE_KEY)

    // Keep in-memory cache in sync.
    _memAccessToken = null
    _memRefreshToken = null
    _memExpiresAt = null
    _memUserId = null
    _memAccountId = undefined
    removeScopedValue(ACCOUNT_ID_BASE_KEY)
    clearAccountIdResolvers()

    if (import.meta.env.DEV) {
      console.log("[Auth] Scoped tokens cleared")
    }
  } catch (e) {
    console.error("[Auth] Failed to clear tokens:", e)
  }
}

export function hasTokens(): boolean {
  return !!getAccessToken() && !!getRefreshToken()
}

// ─────────────────────────────────────────────────────────────────────────────
// Telegram InitData
// ─────────────────────────────────────────────────────────────────────────────

let telegramReadyPromise: Promise<string> | null = null

export function waitForTelegramInitData(): Promise<string> {
  if (telegramReadyPromise) return telegramReadyPromise

  telegramReadyPromise = new Promise((resolve) => {
    // Check immediately
    if (typeof window !== "undefined" && window.Telegram?.WebApp?.initData) {
      if (import.meta.env.DEV)
        console.log("[Auth] initData available immediately")
      resolve(window.Telegram.WebApp.initData)
      return
    }

    // Poll for up to 5 seconds (50 attempts * 100ms)
    const maxAttempts = 50
    let attempts = 0
    const interval = setInterval(() => {
      attempts++
      if (typeof window !== "undefined" && window.Telegram?.WebApp?.initData) {
        clearInterval(interval)
        if (import.meta.env.DEV)
          console.log(`[Auth] initData available after ${attempts * 100}ms`)
        resolve(window.Telegram.WebApp.initData)
      } else if (attempts >= maxAttempts) {
        clearInterval(interval)
        if (import.meta.env.DEV) {
          console.warn("[Auth] Telegram initData not available after 5s")
        }
        telegramReadyPromise = null
        resolve("")
      }
    }, 100)
  })

  return telegramReadyPromise
}

export function getTelegramInitData(): string {
  if (typeof window !== "undefined" && window.Telegram?.WebApp?.initData) {
    return window.Telegram.WebApp.initData
  }
  return ""
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth state
// ─────────────────────────────────────────────────────────────────────────────

let isAuthenticating = false
let authPromise: Promise<boolean> | null = null

export function isAuthenticated(): boolean {
  return hasTokens()
}

export function getAuthPromise(): Promise<boolean> | null {
  return authPromise
}

export function setAuthPromise(promise: Promise<boolean> | null): void {
  authPromise = promise
}

export function setIsAuthenticating(value: boolean): void {
  isAuthenticating = value
}

export function getIsAuthenticating(): boolean {
  return isAuthenticating
}
